import { memo, useEffect, useRef } from 'react';

import { IconPlay } from '@picsart/design-system/foundation/icons/IconPlay';

import useVideoAudioDetection from '@/hooks/useVideoAudioDetection';
import { PreviewMediaType } from '@/types';

import useFeedVideoPlayback from '@/hooks/useFeedVideoPlayback';
import useStyles from './styles';

import type { FeedSlideMediaProps } from './types';

const FeedSlideMedia = ({
  media,
  type,
  isActive,
  shouldPreload,
  shouldShowPoster,
  index,
  isMuted,
  onAudioDetected,
}: FeedSlideMediaProps) => {
  const classes = useStyles();
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = type === PreviewMediaType.VIDEO;

  const { hasAudio, handleVideoLoadedMetadata } = useVideoAudioDetection({
    videoRef,
    isDetectionEnabled: isVideo && shouldPreload,
    videoSourceId: media.url,
  });

  useEffect(() => {
    if (hasAudio) onAudioDetected({ index, hasAudio: true });
  }, [index, hasAudio, onAudioDetected]);

  const { isUserPaused, togglePlayback } = useFeedVideoPlayback({
    videoRef,
    isActive,
    shouldPreload,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.muted = !isActive || isMuted;
  }, [isActive, isMuted]);

  if (!isVideo) {
    return <img className={classes.media} src={media.url} alt="" />;
  }

  const posterUrl = media.poster;

  return (
    <button
      type="button"
      className={classes.videoTapLayer}
      onClick={togglePlayback}
      aria-label={isUserPaused ? 'Play video' : 'Pause video'}
    >
      {posterUrl && shouldShowPoster && (
        <img
          className={classes.poster}
          src={posterUrl}
          alt=""
          aria-hidden
          decoding="sync"
        />
      )}
      <video
        ref={videoRef}
        className={classes.media}
        poster={posterUrl}
        muted
        loop
        playsInline
        preload="none"
        controls={false}
        onLoadedMetadata={handleVideoLoadedMetadata}
      >
        <track kind="captions" />
        <source src={media.url} type="video/mp4" />
      </video>
      {isActive && isUserPaused && (
        <span className={classes.playIndicator} aria-hidden>
          <IconPlay fill="currentColor" />
        </span>
      )}
    </button>
  );
};

export default memo(FeedSlideMedia);
